import { ADMIN_PAGES, ADMIN_PAGE_ROUTES, PermissionLevel } from "../../constants/adminPermissions";

/**
 * Maps a route path to the corresponding admin page permission key
 * Returns null if the route doesn't correspond to an admin page
 */
export const getPermissionKeyFromPath = (pathname: string): string | null => {
  // Remove leading slash and split
  const pathSegments = pathname.split("/").filter((seg) => seg);
  
  // Extract companyId if present (format: companies-xxx-xxx)
  const companyIndex = pathSegments.findIndex((seg) => seg.startsWith("companies-"));
  if (companyIndex === -1) {
    return null; // Not a company route
  }
  
  // Get the segment after companyId
  const segmentsAfterCompany = pathSegments.slice(companyIndex + 1);
  if (segmentsAfterCompany.length === 0) {
    return ADMIN_PAGES.ADMIN_VIEW; // Root company route goes to admin-view
  }
  
  const firstSegment = segmentsAfterCompany[0];
  
  // Check each admin page's route patterns
  for (const [pageKey, routes] of Object.entries(ADMIN_PAGE_ROUTES)) {
    if (routes.some((route) => segmentsAfterCompany.includes(route) || firstSegment === route)) {
      return pageKey;
    }
  }
  
  // Specific route checks
  if (firstSegment === "admin-view") return ADMIN_PAGES.ADMIN_VIEW;
  if (firstSegment === "settings") return ADMIN_PAGES.SETTINGS;
  if (firstSegment === "documents") return ADMIN_PAGES.DOCUMENTS;
  if (firstSegment === "activity") return ADMIN_PAGES.ACTIVITY;
  if (firstSegment === "company-information") return ADMIN_PAGES.COMPANY_INFORMATION;
  if (firstSegment === "investors") return ADMIN_PAGES.INVESTORS;
  if (firstSegment === "redemptions") return ADMIN_PAGES.REDEMPTIONS;
  if (firstSegment === "swaps") return ADMIN_PAGES.SWAPS;
  if (firstSegment === "fundraises") return ADMIN_PAGES.FUNDRAISING;
  if (firstSegment === "distributions") return ADMIN_PAGES.DISTRIBUTIONS;
  if (segmentsAfterCompany.some((seg) => seg === "trading" || segmentsAfterCompany[0] === "trading")) {
    return ADMIN_PAGES.TRADING;
  }
  if (firstSegment === "shares-register" || firstSegment === "options-register" || firstSegment === "notes-register" || firstSegment === "register") {
    return ADMIN_PAGES.REGISTERS;
  }
  if (segmentsAfterCompany.some((seg) => seg === "investor-relations")) {
    return ADMIN_PAGES.INVESTOR_RELATIONS;
  }
  
  return null; // Route doesn't map to a permission-controlled page
};

/**
 * Checks if a user has the required permission level for a page
 */
export const hasPermission = (
  permissionKey: string | null,
  permissions: Record<string, PermissionLevel> | undefined,
): boolean => {
  if (!permissionKey) {
    return true; // Routes without permission keys are always accessible
  }
  
  if (!permissions || Object.keys(permissions).length === 0) {
    return true; // No restrictions = full access
  }
  
  const permission = permissions[permissionKey];
  if (!permission) {
    return true; // Permission not set = full access (MODIFY)
  }
  
  return permission !== PermissionLevel.NONE; // Only NONE blocks access
};

